import { useMemo, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import technologiesServices, {
  CreateTechnologyByNameType,
  TechnologyType,
} from '@/services/technologies';
import AutoCompleteDropdown, { AutoCompleteDropdownType } from '@/trio-ui/AutoCompleteDropdown';
import Modal from '@/trio-ui/Modal';
import { FetchApiIds } from '@/types';

type DropdownOption = {
  value: string;
  label: string;
};

type TechnologiesDropdownType = Omit<AutoCompleteDropdownType, 'options' | 'defaultValue'> & {
  defaultValue: string[];
  isActiveOnly?: boolean;
};

export default function TechnologiesDropdown({
  onChange = () => {},
  name,
  defaultValue,
  isActiveOnly,
  ...props
}: TechnologiesDropdownType): JSX.Element {
  const parseDefaultValues = true;
  const [createModal, setCreateModal] = useState<string | undefined>();
  const technologiesResponse = useApiFetch(
    FetchApiIds.ALL_TECHS,
    technologiesServices.getAllTechnologies
  );
  const createTechnologyMutation = useApiSubmit<TechnologyType, CreateTechnologyByNameType>(
    technologiesServices.createTechnologyByName
  );

  const toast = useToastContext();

  const formattedOptions: DropdownOption[] = useMemo(() => {
    const technologies = technologiesResponse?.data ?? [];
    const filteredTechnologies = isActiveOnly
      ? technologies.filter((tech) => tech.isActive)
      : technologies;

    return filteredTechnologies.map((tech) => ({
      value: tech.id,
      label: tech.name,
    }));
  }, [technologiesResponse.data, isActiveOnly]);

  const onCreateOptionConfirm = async (): Promise<void> => {
    if (!createModal) return;

    setCreateModal(undefined);

    try {
      await createTechnologyMutation.mutateAsync({ name: createModal });
      technologiesResponse.refetch();
      toast.showSuccessFeedback('The new technology has been created.');
    } catch {
      toast.showErrorFeedback('There was a problem to create the technology');
    }
  };

  const onChangeOverwritten = (option: DropdownOption[] | DropdownOption): void => {
    const isArray = Array.isArray(option);
    const value = isArray ? option.map((o) => o.value) : option.value;
    const label = isArray ? option.map((o) => o.label) : option.label;
    return onChange({
      target: { value, id: name, name, label },
      type: 'change',
    });
  };

  const defaultValues = useMemo(() => {
    if (parseDefaultValues) {
      return formattedOptions.filter((option) => {
        if (typeof defaultValue === 'string') {
          return option.value === defaultValue;
        }

        if (Array.isArray(defaultValue)) {
          return defaultValue.includes(option.value);
        }

        return false;
      });
    }
    return defaultValue;
  }, [defaultValue, formattedOptions, parseDefaultValues]);

  return (
    <div>
      <AutoCompleteDropdown
        {...props}
        onChange={onChangeOverwritten}
        name={name}
        onCreateOption={setCreateModal}
        isLoading={technologiesResponse.isLoading || createTechnologyMutation.isLoading}
        options={formattedOptions}
        value={defaultValues as DropdownOption[]}
      />
      <Modal
        isCentered
        size="sm"
        modalTitle="Are you sure?"
        bodyRender={
          <>{`You are creating a new option called "${createModal}". Are you sure you want to create it? This action cannot be undone.`}</>
        }
        onConfirmLabel="Yes, create it"
        onConfirmButtonVariant="blue"
        onConfirmClick={onCreateOptionConfirm}
        onClose={() => setCreateModal(undefined)}
        isOpen={Boolean(createModal)}
      />
    </div>
  );
}
